/**
 * Fable5WaitlistBanner
 * ────────────────────
 * Compact opt-in banner shown on the Legal Pro pricing card. When the user
 * is authenticated, a single click joins the Fable 5 availability waitlist.
 * Anonymous users are taken to register (?intent=fable5) so the post-signup
 * flow can auto-subscribe them.
 *
 * Idempotent: shows "You're on the list" once subscribed; provides "Leave".
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { api } from "@/lib/api";
import { useImperativeLoad } from "@/hooks/useImperativeLoad";
import { Sparkles, Loader2, Check, X } from "lucide-react";
import { toast } from "sonner";

export function Fable5WaitlistBanner() {
  const { t, locale } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  useImperativeLoad(() => {
    if (!user) {
      setSubscribed(false);
      return;
    }
    api.get("/fable5/waitlist/status").then((r) => {
      setSubscribed(!!r.data?.subscribed);
    }).catch(() => setSubscribed(false));
  }, [user?.user_id]);

  const handleJoin = async () => {
    if (!user) {
      navigate("/auth?mode=register&intent=fable5");
      return;
    }
    setLoading(true);
    try {
      await api.post("/fable5/waitlist", { locale });
      setSubscribed(true);
      toast.success(t("fable5.toast_joined"));
    } catch (e) {
      toast.error(t("fable5.toast_join_failed"));
    } finally {
      setLoading(false);
    }
  };

  const handleLeave = async () => {
    setLoading(true);
    try {
      await api.delete("/fable5/waitlist");
      setSubscribed(false);
      toast.info(t("fable5.toast_left"));
    } catch (e) {
      toast.error(t("fable5.toast_leave_failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="mt-5 rounded-xl border border-orange-400/30 bg-orange-500/10 p-4"
      data-testid="fable5-waitlist-banner"
    >
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-orange-500/20 text-orange-300">
          <Sparkles className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.15em] text-orange-300">
            {t("fable5.banner_title")}
            <span className="rounded-full bg-orange-400/20 px-1.5 py-0.5 text-[9px] font-bold text-orange-200">
              BETA
            </span>
          </p>
          <p className="mt-1 text-sm leading-snug text-slate-200">
            {t("fable5.banner_body")}
          </p>
          <div className="mt-3 flex items-center gap-2">
            {subscribed ? (
              <>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-bold text-emerald-300" data-testid="fable5-subscribed">
                  <Check className="h-3.5 w-3.5" />
                  {t("fable5.subscribed")}
                </span>
                <button
                  type="button"
                  onClick={handleLeave}
                  disabled={loading}
                  className="text-[11px] font-semibold text-slate-400 hover:text-white disabled:opacity-50"
                  data-testid="fable5-leave-btn"
                >
                  {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : t("fable5.leave")}
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={handleJoin}
                disabled={loading}
                className="inline-flex items-center gap-1.5 rounded-full bg-orange-500 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-white hover:bg-orange-400 disabled:opacity-60"
                data-testid="fable5-join-btn"
              >
                {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : (
                  <>
                    <Sparkles className="h-3 w-3" />
                    {t("fable5.join_cta")}
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
